import type { Order, StatusFlowEvent } from "./types"

/**
 * Where the parcel of a failed partner delivery is.
 *
 * - returning:    on its way back to Dingoo
 * - returned:     the partner confirmed it is back
 * - noReturn:     not delivered and the partner is not bringing it back
 * - returnFailed: the partner cancelled the return leg; the courier may still
 *                 have the parcel
 *
 * The last two need someone to look at them. A "returned" set by ops by hand is
 * not a partner confirmation, so it stays in noReturn.
 */
export type ExternalReturnState =
	| "returning"
	| "returned"
	| "noReturn"
	| "returnFailed"

export interface ExternalReturnEntry {
	orderId: string
	barcode?: string
	reason?: string
	failedAt?: string
	state: ExternalReturnState
}

export const isExternalReturnWarning = (state: ExternalReturnState): boolean =>
	state === "noReturn" || state === "returnFailed"

export const getExternalReturnState = (
	order: Order
): ExternalReturnState | null => {
	const ed = order.externalDelivery
	if (!ed?.partner) return null
	if (ed.returned) return "returned"
	if (ed.returnStatus === "failed") return "returnFailed"
	// status "returning" is how failures looked before returns had their own fields
	if (ed.returnStatus === "returning" || ed.status === "returning")
		return "returning"
	if (ed.status === "not_delivered" && !ed.returnDeliveryId) {
		// Back then an Uber delivery only reached not_delivered once the parcel
		// was back, and that event carried undeliverableAction "returned".
		return ed.undeliverableAction === "returned" ? "returned" : "noReturn"
	}
	if (order.status === "not_delivered" || order.status === "returned")
		return "noReturn"
	return null
}

const flowTimestamp = (event: StatusFlowEvent): string | undefined =>
	typeof event.timestamp === "string" ? event.timestamp : event.timestamp?.$date

export const toExternalReturnEntry = (
	order: Order
): ExternalReturnEntry | null => {
	const state = getExternalReturnState(order)
	if (!state) return null
	const ed = order.externalDelivery!
	const failure = ed.statusFlow?.find(
		(event) => event.status === "not_delivered" || event.status === "returning"
	)
	return {
		orderId: order.orderId,
		barcode:
			order.confirmedBarcode || ed.pickupVerification?.barcodes?.[0]?.value,
		reason: ed.undeliverableReason || order.notDeliveredReason,
		failedAt: failure ? flowTimestamp(failure) : undefined,
		state,
	}
}

/** Every order an address carries: its own and the ones bundled with it. */
export const toExternalReturnEntries = (
	orders: Order[]
): ExternalReturnEntry[] =>
	orders
		.flatMap((order) => [order, ...(order.bundledOrders || [])])
		.map(toExternalReturnEntry)
		.filter((entry): entry is ExternalReturnEntry => entry !== null)
